import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  AlertTriangle,
  Target,
  Building2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  getMyOrganizations,
  getOrganizationSiteTree,
  OrgWithPermission,
} from "../services/organization.ts";
import {
  getAlertRules,
  createAlertRule,
  updateAlertRule,
  deleteAlertRule,
  batchUpdateAlertRules,
} from "../services/alertService.ts";
import {
  Site,
  AlertRule,
  AlertRulePayload,
  MultipleSitesAlertRuleFormData,
  BatchUpdateAlertRulePayload,
} from "../types";
import messageService from "../components/Message/message.ts";
import AlertRuleModal from "../components/Modals/AlertRuleModal.tsx";
import BulkEditModal from "../components/Modals/BulkEditModal.tsx";
import DeleteConfirmModal from "../components/Modals/DeleteConfirmModal.tsx";

// Extended form data interface for email addresses
interface ExtendedFormData extends MultipleSitesAlertRuleFormData {
  emailAddresses: string[];
}

const SiteHealthAlertRules: React.FC = () => {
  const [organizations, setOrganizations] = useState<OrgWithPermission[]>([]);
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrgWithPermission | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAlertRuleModal, setShowAlertRuleModal] = useState(false);
  const [editingAlertRule, setEditingAlertRule] = useState<AlertRule | null>(
    null
  );
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{
    alertRule: AlertRule;
    bulkRules?: AlertRule[];
  } | null>(null);
  const [selectedRules, setSelectedRules] = useState<Set<string>>(new Set());
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditData, setBulkEditData] = useState<{
    name?: string;
    description?: string;
    condition?: {
      metric?: string;
      operator?: string;
      threshold?: number;
    };
    severity?: string;
    emailAddresses?: string[];
  }>({});

  // Updated form data for the new workflow with email addresses
  const [formData, setFormData] = useState<ExtendedFormData>({
    alertRule: {
      name: "",
      description: "",
      condition: {
        metric: "soc",
        operator: "lt",
        threshold: 0,
      },
      severity: "medium",
    },
    selectedSites: [],
    emailAddresses: [],
  });

  // Operator mapping for display
  const operatorMap: Record<string, string> = {
    ">": "greater than",
    "<": "less than",
    "==": "equals",
    ">=": "greater than or equal",
    "<=": "less than or equal",
    "!=": "not equals",
    gt: "greater than",
    lt: "less than",
    eq: "equals",
    gte: "greater than or equal",
    lte: "less than or equal",
    ne: "not equals",
  };

  // Metric display mapping
  const metricDisplayMap: Record<string, string> = {
    soc: "Battery SOC (%)",
    battery_voltage: "Battery Voltage (V)",
    solar_power: "Solar Power (W)",
    status: "Device Status",
  };

  // IQWatch device metrics only
  // Removed unused metricOptions

  // Reset form data when modal opens/closes
  useEffect(() => {
    if (showAlertRuleModal) {
      if (editingAlertRule) {
        // Populate form with existing alert rule data
        const allowedMetrics = [
          "soc",
          "battery_voltage",
          "solar_power",
          "status",
        ] as const;
        const metric = allowedMetrics.includes(
          editingAlertRule.rule_type as any
        )
          ? (editingAlertRule.rule_type as (typeof allowedMetrics)[number])
          : "soc";

        const allowedOperators = [
          "gt",
          "lt",
          "eq",
          "gte",
          "lte",
          "ne",
        ] as const;
        const operator = allowedOperators.includes(
          editingAlertRule.condition as any
        )
          ? (editingAlertRule.condition as (typeof allowedOperators)[number])
          : "lt";

        setFormData({
          alertRule: {
            name: editingAlertRule.name || "",
            description: editingAlertRule.description || "",
            condition: {
              metric,
              operator,
              threshold: editingAlertRule.threshold || 0,
            },
            severity: editingAlertRule.is_active ? "medium" : "low",
          },
          selectedSites: [editingAlertRule.site.id],
          emailAddresses:
            editingAlertRule.email_addresses ||
            editingAlertRule.notification_users ||
            [],
        });
      } else {
        // Reset form for new alert rule
        setFormData({
          alertRule: {
            name: "",
            description: "",
            condition: {
              metric: "soc",
              operator: "lt",
              threshold: 0,
            },
            severity: "medium",
          },
          selectedSites: [],
          emailAddresses: [],
        });
      }
    }
  }, [showAlertRuleModal, editingAlertRule]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMyOrganizations();
      setOrganizations(data);

      // Auto-select first organization if available
      if (data.length > 0) {
        setSelectedOrganization(data[0]);
        setExpandedOrgs(new Set([data[0].id]));
      }
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationData = useCallback(async (orgId: string) => {
    try {
      setError(null);

      // Fetch site tree and alert rules
      const [siteTreeResponse] = await Promise.all([
        getOrganizationSiteTree(orgId),
      ]);

      // Convert API Site type to frontend Site type
      const convertedSites = siteTreeResponse.sites.map((site) => ({
        ...site,
        deviceCount: 0,
        createdAt: site.created_at,
        updatedAt: site.updated_at,
      })) as Site[];

      setSites(convertedSites);
      await fetchAlertRules(orgId);
    } catch (error) {
      console.error("Failed to fetch organization data:", error);
      setError("Failed to load organization data");
    }
  }, []);

  const fetchAlertRules = async (orgId: string) => {
    try {
      const response = await getAlertRules({ orgId });
      setAlertRules(response.rules || []);
    } catch (error) {
      console.error("Failed to fetch alert rules:", error);
    }
  };

  // useEffect hooks after function definitions
  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrganization) {
      fetchOrganizationData(selectedOrganization.id);
    }
  }, [selectedOrganization, fetchOrganizationData]);

  const handleCreateAlertRule = () => {
    setEditingAlertRule(null);
    setShowAlertRuleModal(true);
  };

  const handleEditAlertRule = (alertRule: AlertRule) => {
    setEditingAlertRule(alertRule);
    setShowAlertRuleModal(true);
  };

  const handleSaveAlertRule = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrganization || formData.selectedSites.length === 0) {
      setError("Please select at least one site");
      return;
    }

    try {
      setModalLoading(true);
      setError(null);

      if (editingAlertRule) {
        // Single site update for existing rule
        const payload: AlertRulePayload = {
          name: formData.alertRule.name,
          description: formData.alertRule.description || "",
          org_id: selectedOrganization.id,
          site_id: formData.selectedSites[0], // Use first selected site for edit
          device_type: "iqwatch",
          rule_type: formData.alertRule.condition.metric,
          condition: formData.alertRule.condition.operator,
          threshold: Number(formData.alertRule.condition.threshold),
          field_path: formData.alertRule.condition.metric,
          email_enabled: formData.emailAddresses.length > 0,
          email_addresses: formData.emailAddresses,
          cooldown_minutes: 30,
        };

        await updateAlertRule(editingAlertRule.id, payload);
        messageService.success("Alert rule updated successfully!");
      } else {
        // Create rules for multiple sites
        const promises = formData.selectedSites.map((siteId) => {
          const payload: AlertRulePayload = {
            name: formData.alertRule.name,
            description: formData.alertRule.description || "",
            org_id: selectedOrganization.id,
            site_id: siteId,
            device_type: "iqwatch",
            rule_type: formData.alertRule.condition.metric,
            condition: formData.alertRule.condition.operator,
            threshold: Number(formData.alertRule.condition.threshold),
            field_path: formData.alertRule.condition.metric,
            email_enabled: formData.emailAddresses.length > 0,
            email_addresses: formData.emailAddresses,
            cooldown_minutes: 30,
          };
          return createAlertRule(payload);
        });

        await Promise.all(promises);
        messageService.success(
          `Alert rule created successfully for ${formData.selectedSites.length} site(s)!`
        );
      }

      setShowAlertRuleModal(false);
      setEditingAlertRule(null);
      fetchAlertRules(selectedOrganization.id);
    } catch (error: any) {
      console.error("Failed to save alert rule:", error);
      setError(
        error.response?.data?.message ||
          `Failed to ${editingAlertRule ? "update" : "create"} alert rule`
      );
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteAlertRule = (alertRule: AlertRule) => {
    setShowDeleteConfirm({ alertRule });
  };

  const confirmDeleteAlertRule = async () => {
    if (!showDeleteConfirm) return;

    try {
      const rulesToDelete = showDeleteConfirm.bulkRules || [
        showDeleteConfirm.alertRule,
      ];

      await Promise.all(rulesToDelete.map((rule) => deleteAlertRule(rule.id)));

      messageService.success(
        rulesToDelete.length > 1
          ? `${rulesToDelete.length} alert rules deleted successfully!`
          : "Alert rule deleted successfully!"
      );

      if (selectedOrganization) {
        fetchAlertRules(selectedOrganization.id);
      }
      setSelectedRules(new Set()); // Clear selection after delete
    } catch (error: any) {
      console.error("Failed to delete alert rule(s):", error);
      setError(
        error.response?.data?.message || "Failed to delete alert rule(s)"
      );
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const cancelDeleteAlertRule = () => {
    setShowDeleteConfirm(null);
  };

  const handleCloseModal = () => {
    setShowAlertRuleModal(false);
    setEditingAlertRule(null);
  };

  const handleOrganizationSelect = (org: OrgWithPermission) => {
    setSelectedOrganization(org);
    setExpandedOrgs(new Set([org.id]));
  };

  const toggleOrganizationExpansion = (orgId: string) => {
    const newExpanded = new Set(expandedOrgs);
    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
    }
    setExpandedOrgs(newExpanded);
  };

  // Helper function to get sites recursively
  const getAllSites = (sites: Site[]): Site[] => {
    const result: Site[] = [];
    const traverse = (site: Site) => {
      result.push(site);
      if (site.children) {
        site.children.forEach(traverse);
      }
    };
    sites.forEach(traverse);
    return result;
  };

  const allSites = getAllSites(sites);

  // Filter alert rules based on search term
  const filteredAlertRules = alertRules.filter(
    (rule) =>
      rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.rule_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group rules by configuration (name + rule settings + email settings)
  const groupRulesByConfiguration = (rules: AlertRule[]) => {
    const groups: Record<string, AlertRule[]> = {};

    rules.forEach((rule) => {
      // Normalize condition operator to handle both symbol and abbreviated formats
      const normalizedCondition = normalizeCondition(rule.condition);

      // Include email settings in grouping key to prevent separation when email recipients change
      const emailKey = rule.email_addresses?.sort().join(",") || "";
      const key = `${rule.name}_${rule.rule_type}_${normalizedCondition}_${rule.threshold}_${emailKey}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(rule);
    });

    return groups;
  };

  // Helper function to normalize condition operators
  const normalizeCondition = (condition: string): string => {
    const conditionMap: Record<string, string> = {
      ">": "gt",
      "<": "lt",
      "==": "eq",
      ">=": "gte",
      "<=": "lte",
      "!=": "ne",
      // Already normalized
      gt: "gt",
      lt: "lt",
      eq: "eq",
      gte: "gte",
      lte: "lte",
      ne: "ne",
    };

    return conditionMap[condition] || condition;
  };

  const ruleGroups = groupRulesByConfiguration(filteredAlertRules);

  // Bulk operations handlers
  const handleSelectRule = (ruleId: string, checked: boolean) => {
    const newSelected = new Set(selectedRules);
    if (checked) {
      newSelected.add(ruleId);
    } else {
      newSelected.delete(ruleId);
    }
    setSelectedRules(newSelected);
  };

  const handleSelectGroup = (rules: AlertRule[], checked: boolean) => {
    const newSelected = new Set(selectedRules);
    rules.forEach((rule) => {
      if (checked) {
        newSelected.add(rule.id);
      } else {
        newSelected.delete(rule.id);
      }
    });
    setSelectedRules(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRules(new Set(filteredAlertRules.map((rule) => rule.id)));
    } else {
      setSelectedRules(new Set());
    }
  };

  const handleBulkEdit = () => {
    if (selectedRules.size === 0) return;

    // Get the first selected rule to pre-populate the form
    const firstRule = filteredAlertRules.find((rule) =>
      selectedRules.has(rule.id)
    );
    if (firstRule) {
      setBulkEditData({
        name: firstRule.name,
        description: firstRule.description || "",
        condition: {
          metric: firstRule.rule_type,
          operator: firstRule.condition,
          threshold: Number(firstRule.threshold),
        },
        severity: firstRule.is_active ? "medium" : "low",
        emailAddresses:
          firstRule.email_addresses || firstRule.notification_users || [],
      });
    }
    setShowBulkEditModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedRules.size === 0) return;

    const rulesToDelete = filteredAlertRules.filter((rule) =>
      selectedRules.has(rule.id)
    );
    setShowDeleteConfirm({
      alertRule: rulesToDelete[0],
      bulkRules: rulesToDelete,
    });
  };

  const handleSaveBulkEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrganization || selectedRules.size === 0) {
      setError("No rules selected for bulk edit");
      return;
    }

    try {
      setModalLoading(true);
      setError(null);

      // Prepare update data (only include fields that have values)
      const updateData: Partial<AlertRulePayload> = {};

      if (bulkEditData.name) {
        updateData.name = bulkEditData.name;
      }
      if (bulkEditData.description !== undefined) {
        updateData.description = bulkEditData.description;
      }
      if (bulkEditData.condition?.metric) {
        updateData.rule_type = bulkEditData.condition.metric;
        updateData.field_path = bulkEditData.condition.metric;
      }
      if (bulkEditData.condition?.operator) {
        updateData.condition = bulkEditData.condition.operator;
      }
      if (bulkEditData.condition?.threshold !== undefined) {
        updateData.threshold = bulkEditData.condition.threshold;
      }
      if (bulkEditData.emailAddresses !== undefined) {
        updateData.email_addresses = bulkEditData.emailAddresses;
        updateData.email_enabled = bulkEditData.emailAddresses.length > 0;
      }

      // Always include org_id and device_type for consistency
      updateData.org_id = selectedOrganization.id;
      updateData.device_type = "iqwatch";
      updateData.cooldown_minutes = 30;

      const payload: BatchUpdateAlertRulePayload = {
        ruleIds: Array.from(selectedRules),
        updateData,
      };

      await batchUpdateAlertRules(payload);

      messageService.success(
        `${selectedRules.size} alert rules updated successfully!`
      );

      setShowBulkEditModal(false);
      setSelectedRules(new Set());
      setBulkEditData({});
      fetchAlertRules(selectedOrganization.id);
    } catch (error: any) {
      console.error("Failed to bulk update alert rules:", error);
      setError(error.response?.data?.message || "Failed to update alert rules");
    } finally {
      setModalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            Loading site health alert rules...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with bulk actions */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Site Health Alert Rules
              </h1>
              <p className="text-gray-600 mt-1">
                Manage device health alert rules at the site level across all
                customers
              </p>
            </div>
            <div className="flex space-x-3">
              {selectedRules.size > 0 && (
                <>
                  <span className="text-sm text-gray-600 self-center">
                    {selectedRules.size} selected
                  </span>
                  <button
                    onClick={handleBulkEdit}
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Bulk Edit
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected
                  </button>
                </>
              )}
              {selectedOrganization && (
                <button
                  onClick={() => handleCreateAlertRule()}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Site Alert Rule
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Organization Selector */}
          <div className="col-span-3">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900">Customers</h3>
              </div>
              <div className="p-2 max-h-96 overflow-y-auto">
                {organizations.map((org) => (
                  <div key={org.id} className="mb-1">
                    <button
                      onClick={() => handleOrganizationSelect(org)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                        selectedOrganization?.id === org.id
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm font-medium truncate">
                          {org.name}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOrganizationExpansion(org.id);
                        }}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        {expandedOrgs.has(org.id) ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                      </button>
                    </button>
                    {expandedOrgs.has(org.id) &&
                      selectedOrganization?.id === org.id && (
                        <div className="ml-6 mt-1 text-xs text-gray-600">
                          {allSites.length} sites • {alertRules.length} alert
                          rules
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            {!selectedOrganization ? (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select a Customer
                </h3>
                <p className="text-gray-600">
                  Choose a customer from the left panel to manage their site
                  health alert rules.
                </p>
              </div>
            ) : (
              <>
                {/* Search and bulk select controls */}
                <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="relative flex-1 mr-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search alert rules..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            filteredAlertRules.length > 0 &&
                            selectedRules.size === filteredAlertRules.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Select All
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Alert Rules List with grouping and selection */}
                <div className="space-y-6">
                  {Object.keys(ruleGroups).length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                      <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No Site Alert Rules Found
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Get started by creating your first site-level alert rule
                        for {selectedOrganization.name}.
                      </p>
                      <button
                        onClick={() => handleCreateAlertRule()}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Alert Rule
                      </button>
                    </div>
                  ) : (
                    Object.values(ruleGroups).map((rules) => {
                      const groupKey = rules.map((r) => r.id).join("-");
                      const isGroupSelected = rules.every((rule) =>
                        selectedRules.has(rule.id)
                      );
                      const isGroupPartiallySelected =
                        rules.some((rule) => selectedRules.has(rule.id)) &&
                        !isGroupSelected;

                      return (
                        <div
                          key={groupKey}
                          className="bg-white rounded-lg shadow-sm border"
                        >
                          <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={isGroupSelected}
                                  ref={(el) => {
                                    if (el)
                                      el.indeterminate =
                                        isGroupPartiallySelected;
                                  }}
                                  onChange={(e) =>
                                    handleSelectGroup(rules, e.target.checked)
                                  }
                                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-3"
                                />
                                <div>
                                  <h3 className="text-lg font-medium text-gray-900">
                                    {rules[0].name}
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {rules.length > 1
                                      ? `Applied to ${rules.length} sites`
                                      : `Applied to ${rules[0].site.name}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                {rules.length > 1 && (
                                  <button
                                    onClick={() => {
                                      setSelectedRules(
                                        new Set(rules.map((r) => r.id))
                                      );
                                      handleBulkEdit();
                                    }}
                                    className="inline-flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                  >
                                    <Edit className="w-3 h-3 mr-1" />
                                    Edit Group
                                  </button>
                                )}
                                <button
                                  onClick={() => handleCreateAlertRule()}
                                  className="inline-flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Rule
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="divide-y divide-gray-200">
                            {rules.map((rule) => (
                              <div key={rule.id} className="px-6 py-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center flex-1 min-w-0">
                                    <input
                                      type="checkbox"
                                      checked={selectedRules.has(rule.id)}
                                      onChange={(e) =>
                                        handleSelectRule(
                                          rule.id,
                                          e.target.checked
                                        )
                                      }
                                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-3"
                                    />
                                    <AlertTriangle
                                      className={`w-5 h-5 mr-3 ${
                                        rule.is_active
                                          ? "text-orange-500"
                                          : "text-gray-400"
                                      }`}
                                    />
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-900">
                                        {rule.site.name}
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        {metricDisplayMap[rule.rule_type] ||
                                          rule.rule_type}{" "}
                                        {operatorMap[rule.condition] ||
                                          rule.condition}{" "}
                                        {rule.threshold}
                                      </p>
                                      {rule.description && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          {rule.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span
                                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        rule.is_active
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {rule.is_active ? "Active" : "Inactive"}
                                    </span>
                                    <button
                                      onClick={() => handleEditAlertRule(rule)}
                                      className="text-blue-600 hover:text-blue-900 p-1"
                                      title="Edit"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteAlertRule(rule)
                                      }
                                      className="text-red-600 hover:text-red-900 p-1"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Alert Rule Modal */}
        {selectedOrganization && (
          <AlertRuleModal
            isOpen={showAlertRuleModal}
            onClose={handleCloseModal}
            onSubmit={handleSaveAlertRule}
            formData={formData}
            setFormData={setFormData}
            selectedOrganization={selectedOrganization}
            allSites={allSites}
            editingAlertRule={editingAlertRule}
            modalLoading={modalLoading}
          />
        )}

        {/* Bulk Edit Modal */}
        <BulkEditModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          onSubmit={handleSaveBulkEdit}
          bulkEditData={bulkEditData}
          setBulkEditData={setBulkEditData}
          selectedRulesCount={selectedRules.size}
          modalLoading={modalLoading}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={!!showDeleteConfirm}
          onConfirm={confirmDeleteAlertRule}
          onCancel={cancelDeleteAlertRule}
          alertRule={showDeleteConfirm?.alertRule!}
          bulkRules={showDeleteConfirm?.bulkRules}
        />
      </div>
    </div>
  );
};

export default SiteHealthAlertRules;
